import { LocationStrategy } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import * as URI from 'urijs';

import { State } from '®app/ngrx';
import {
  CONTAINER_MODE,
  ContainerModes,
  LIST_ID,
} from '®shared/container-structure';
import {
  CurrentUserAndPermissions,
  CurrentUserFactoryProvider,
} from '®shared/current-user';
import { EventsService } from '®shared/events';
import {
  LAUNCH_METHOD,
  LaunchMethod,
  LaunchMethodFactoryProvider,
} from '®shared/events/launch-method.constant';
import {
  LAUNCH_MODE,
  LaunchMode,
  getValidatedLaunchMode,
} from '®shared/events/launch-mode.constant';
import { GA_PAGE_NAMES } from '®shared/google-analytics';
import { GoogleAnalyticsGA4Service } from '®shared/google-analytics/google-analytics-ga4';
import { UserPermissions } from '®shared/permissions';
import { createListEntryPointEvent } from '®shared/reading-list/events';
import { ListItem, ReadingListDetails } from '®shared/reading-list/models';
import { InitialiseWithTenantAction } from '®shared/tenant';
import { UriService } from '®shared/url-utils';

export type ItemRouteData = {
  currentUser: CurrentUserAndPermissions;
  listItem: ListItem;
  isDraft: boolean;
};

export function getListIdFromItem(route: ActivatedRoute): string {
  return route.snapshot.data.listItem.list.id;
}

export function getLaunchModeFromRoute(route: ActivatedRoute): LaunchMode {
  const { data } = route.snapshot;
  const { isDraft = false } = data;
  return getValidatedLaunchMode(ContainerModes.View, isDraft);
}

@Component({
  selector: 'rl-item-view',
  templateUrl: './item-view.component.html',
  providers: [
    { provide: CONTAINER_MODE, useValue: ContainerModes.View },
    CurrentUserFactoryProvider,
    LaunchMethodFactoryProvider,
    {
      provide: LAUNCH_MODE,
      useFactory: getLaunchModeFromRoute,
      deps: [ActivatedRoute],
    },
    {
      provide: LIST_ID,
      useFactory: getListIdFromItem,
      deps: [ActivatedRoute],
    },
  ],
})
export class ItemViewComponent implements OnInit {
  item: ListItem;
  isDraft: boolean;
  listUrl: string;
  resourceEditUrl: string;
  permissions: UserPermissions;

  constructor(
    private eventsService: EventsService,
    private googleAnalyticsGA4: GoogleAnalyticsGA4Service,
    @Inject(LAUNCH_METHOD) private launchMethod: LaunchMethod,
    @Inject(LAUNCH_MODE) private launchMode: LaunchMode,
    @Inject(LIST_ID) public listId: string,
    private locationStrategy: LocationStrategy,
    private route: ActivatedRoute,
    private store: Store<State>,
    private titleService: Title,
    private uriService: UriService,
  ) {}

  ngOnInit() {
    this.route.data.forEach(this.extractDataFromActivatedRoute.bind(this));
  }

  /**
   * Extracts data from the ActivatedRoute and sets up the component
   * @param {ItemRouteData} data - resolved list item and tenant
   */
  private extractDataFromActivatedRoute(data: ItemRouteData): void {
    const { currentUser, listItem, isDraft } = data;
    const tenant = listItem.tenant;
    this.store.dispatch(new InitialiseWithTenantAction(tenant));

    this.permissions = currentUser.permissions;

    this.item = listItem;
    this.isDraft = isDraft;

    this.setWindowTitle(
      `Shared item from ${this.item.list.title}`,
      tenant.name,
    );

    const currentUrl = this.uriService.getTarlItemViewUrl(
      this.item.id,
      isDraft,
    );

    const isLoggedIn = !currentUser.oauthUser.isAnonymous;
    this.googleAnalyticsGA4.sendFakePageView(
      currentUrl,
      GA_PAGE_NAMES.ITEM_VIEW,
      isLoggedIn,
    );

    this.listUrl = this.buildListUrl(tenant.short_code, this.item.list);

    this.resourceEditUrl = this.uriService.getResourceEditUrlForItem(
      this.item.resource.id,
      this.item.id,
    );

    this.eventsService.recordEvent(
      createListEntryPointEvent(
        this.listId,
        { type: 'items', id: this.item.id },
        this.launchMethod,
        this.launchMode,
      ),
    );
  }

  /**
   * A utility function to create a link back to the list from whence the item came
   * @param {string} shortCode The short code as defined in the tenant config
   * @param {ReadingListDetails} listId    The list ID as set in the bound item's list field
   * @return {string} The URI to view the list
   */
  buildListUrl(shortCode: string, list: ReadingListDetails): string {
    return new URI()
      .segment([
        this.locationStrategy.getBaseHref(),
        '3',
        shortCode,
        'lists',
        list.id,
      ])
      .suffix('html')
      .toString();
  }

  /**
   * Sets the document title using Angular's title service. Formatted typically as 'Page title | Tenancy name'
   * @param {string} newTitle The string to sit in front of the pipe
   * @param {string} tenantName Customise the title display after the pipe
   */
  setWindowTitle(newTitle: string, tenantName: string): void {
    this.titleService.setTitle(`${newTitle} | ${tenantName}`);
  }
}
